import React from 'react';
import { Avatar } from '@framework-controls/core';
import * as css from './AppointmentCard.module.scss';
import { Appointment, Transportation } from 'src/models';
import { strings } from 'src/localization';
import { ViewMode } from 'src/enums';
import { DateTime } from '@reynolds/datetime';
import classNames from 'classnames';
import NoProfile from 'src/content/icons/Profile2.svg';
import { IconProfile2 } from 'src/content/icons';
import moment from 'moment';
import 'moment/min/locales.min';

export interface IProps {
    advisorPhotoPath: string;
    appointment: Appointment;
    advImgAltTag: string;
    viewMode?: ViewMode;
    theme: string;
    transIndex?: Transportation;
    locale: string;
    modifyAppointment?: () => void;
}

const AppointmentCard = (props: IProps) => {
    const appointment: Appointment = props.appointment;
    const dateValue = new DateTime(new Date(props.appointment.date));
    const displayDate = dateValue.format('DD, YYYY');
    const displayTime = props.appointment.time;
    const acctMode = props.viewMode === ViewMode.CustAcct;
    const editApptMode = props.viewMode === ViewMode.EditAppt;

    const processingDate = dateValue.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dayOfWeekLit = processingUTCDate.format('dddd');
    const MonthLit = processingUTCDate.format('MMMM');

    let flexBoxClassName = classNames(
        css.flexBox,
        {
            [css.flexBoxAcct]: acctMode,
        },
        {
            [css.flexBoxMini]: props.theme === 'mini',
        },
        {
            [css.flexBoxCadillac]: props.theme === 'cadillac',
        },
    );
    const dateRowClassName = classNames(
        css.dateRow,
        {
            [css.dateRowAcct]: acctMode,
            [css.dateRowKia]: props.theme === 'kia',
            [css.dateRowHonda]: props.theme === 'honda',
            [css.dateRowBMW]: props.theme === 'bmw',
            [css.dateRowFord]: props.theme === 'ford',


        },
    );
    // PMR 1462985 requested that we always underline the link
    let linkButtonClassName = css.summaryEditLinkADA;
    if (props.theme === 'colorSensitive' || props.theme === 'lexus') {
        // NoProfileImage = NoProfileAccess;
        linkButtonClassName = css.summaryEditLinkADA;
        flexBoxClassName = css.flexBoxADA;
        if (acctMode) {
            flexBoxClassName = css.flexBoxAcct;
        }
    }
    if (props.theme === 'chevrolet') {
        linkButtonClassName = css.summaryEditLinkChevrolet;
    }
    if (props.theme === 'bmw') {
        linkButtonClassName = css.summaryEditLinkBMW;
    }

    const summaryTitleClassName = classNames(
        css.summaryTitle,
        {
            [css.summaryTitleAudi]: props.theme === 'audi',
            [css.summaryTitleHonda]: props.theme === 'honda',
            [css.summaryTitleBMW]: props.theme === 'bmw',
            [css.summaryTitleFord]: props.theme === 'ford',

        },
    );

    const dateRowAStyles = classNames(
        css.dateRowA,
        {
            [css.dateRowABMW]: props.theme === 'bmw',
            [css.dateRowAFord]: props.theme === 'ford',


        },
    );
    const image = props.advisorPhotoPath !== ('' || undefined) ? props.advisorPhotoPath : '';
    let apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate + ' | ' + displayTime;

    let header = (<h4 className={summaryTitleClassName}>{strings.Appointment}</h4>);
    if (acctMode) {
        header = (<div className={dateRowAStyles} id='dateTime'>{apptDate}</div>);
    } else if (editApptMode && props.modifyAppointment) {
        header = (
            <div style={{display: 'flex'}}>
                <h4 className={summaryTitleClassName}>{strings.Appointment}</h4>
                <div onClick={props.modifyAppointment} className={linkButtonClassName}>{strings.ModifyAppointment}</div>
            </div>
        );
    }

    let lastLine = props.transIndex?.title;
    if (props.appointment.transFlag === 'N') {
        apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate;
        lastLine = strings.DropOff + displayTime;
    }
    const transRow = classNames (
        css.transRow,
        {
            [css.transRowLexus]: (props.theme === 'lexus'),
            [css.transRowHonda]: (props.theme === 'honda'),
            [css.transRowBMW]: (props.theme === 'bmw'),
            [css.transRowFord]: (props.theme === 'ford'),


        },
    );

    const advRow = classNames (
        css.advRow,
        {
            [css.advRowCV]: (props.theme === 'chevrolet'),
            [css.advRowBMW]: (props.theme === 'bmw'),
            [css.advRowFord]: (props.theme === 'ford'),


        },
    );

    const apptIcon = classNames (
        css.apptIcon,
        {
            [css.apptIconCadillac]: (props.theme === 'cadillac'),
        },
    );

    const withDivStyles = classNames (
        css.WithDiv,
        {
            [css.WithDivBMW]: (props.theme === 'bmw'),
            [css.WithDivFord]: (props.theme === 'ford'),


        },
    );

    const renderAvatar = () => {
        if (image) {
            return (
                <Avatar
                    src={image}
                    elevation={0}
                    size={60}
                    color='blank'
                    alt={props.advImgAltTag}
                    borderThickness={0}
                />
            );
        }

        if (acctMode && props.theme === 'kia') {
            return (
                <Avatar
                    src={NoProfile}
                    elevation={0}
                    size={60}
                    color='blank'
                    alt={props.advImgAltTag}
                    borderThickness={0}
                />
            );
        }

        return (
            <div className={`${props.theme === 'lexusMonogram' ? `${css.apptIconLexusMonogram}` : `${apptIcon}`}`}>
                <IconProfile2
                    style={{
                        height: '60px',
                        width: '60px',
                    }}
                />
            </div>
        );
    };

    return (
        <div>
            {header}
            <div className={css.summaryCard + ' ' + flexBoxClassName}>
                {renderAvatar()}
                <div className={css.appointmentData}>
                    {acctMode ?
                        (<div className={withDivStyles}>{strings.With + ' '}</div>) : null
                    }
                    <div className={dateRowClassName} id='dateTime'>{apptDate}</div>
                    <div className={props.theme === 'honda' ? css.advRowHonda : advRow} id='advisor'>{appointment.advName}</div>
                    <div className={transRow} id='transportation'>{lastLine}</div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentCard;
